import { useState, useEffect } from 'react';
import { Form, DatePicker, Button, Space, Select, Input, Tooltip } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { toSelectOptions, PROUD_RESULTS, WENT_WELL, DIFFICULTIES, HELPS_EFFECTIVE, HINDERS_WORK, COLLEAGUE_DIFFICULTIES, COLLEAGUE_ISSUES, PROCESSES_TO_IMPROVE, RECENT_LEARNING, DEVELOPMENT_SUPPORT, DISCUSSION_TOPICS, FEEDBACK_NEEDED, MANAGER_FEEDBACK, MEETING_MAIN_RESULT } from '../../utils/validation';
import { ScoreSlider, MultiSelectField, StateWordPicker } from './FormFields';
import FormSection from './FormSection';

const { TextArea } = Input;

const optionalLabel = (text) => (
  <>
    {text}
    <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 6 }}>(необязательно)</span>
  </>
);

const FormBuilder = ({
  initialValues,
  onSubmit,
  loading,
  isEdit = false,
  optional = true,
  variant = 'default',
}) => {
  const [form] = Form.useForm();
  const [moodScore, setMoodScore] = useState(initialValues?.moodScore ?? 7);
  const [twoWeeksScore, setTwoWeeksScore] = useState(initialValues?.twoWeeksScore ?? 7);
  const [workloadLevel, setWorkloadLevel] = useState(initialValues?.workloadLevel ?? 5);
  const [stressLevel, setStressLevel] = useState(initialValues?.stressLevel ?? 5);
  const [jobSatisfaction, setJobSatisfaction] = useState(initialValues?.jobSatisfaction ?? 7);
  const [teamSatisfaction, setTeamSatisfaction] = useState(initialValues?.teamSatisfaction ?? 7);

  const colleagueDifficulties = Form.useWatch('colleagueDifficulties', form);
  const showColleagueIssues =
    colleagueDifficulties === 'minor' || colleagueDifficulties === 'significant';

  useEffect(() => {
    if (initialValues) {
      setMoodScore(initialValues.moodScore ?? 7);
      setTwoWeeksScore(initialValues.twoWeeksScore ?? 7);
      setWorkloadLevel(initialValues.workloadLevel ?? 5);
      setStressLevel(initialValues.stressLevel ?? 5);
      setJobSatisfaction(initialValues.jobSatisfaction ?? 7);
      setTeamSatisfaction(initialValues.teamSatisfaction ?? 7);
    }
  }, [initialValues]);

  const req = (rules) => (optional ? [] : rules);

  const handleFinish = (values) => {
    onSubmit({
      ...values,
      fillDate: (values.fillDate || dayjs()).toISOString(),
      moodScore,
      twoWeeksScore,
      workloadLevel,
      stressLevel,
      jobSatisfaction,
      teamSatisfaction,
      colleagueIssues: showColleagueIssues ? values.colleagueIssues || [] : [],
      proudResults: values.proudResults || [],
      wentWell: values.wentWell || [],
      difficulties: values.difficulties || [],
      helpsEffective: values.helpsEffective || [],
      hindersWork: values.hindersWork || [],
      processesToImprove: values.processesToImprove || [],
      recentLearning: values.recentLearning || [],
      developmentSupport: values.developmentSupport || [],
      discussionTopics: values.discussionTopics || [],
      feedbackNeeded: values.feedbackNeeded || [],
      managerFeedback: values.managerFeedback || [],
      meetingMainResult: values.meetingMainResult || [],
    });
  };

  const defaultValues = initialValues
    ? {
        ...initialValues,
        fillDate: initialValues.fillDate ? dayjs(initialValues.fillDate) : dayjs(),
        colleagueIssues: initialValues.colleagueIssues || [],
      }
    : {
        fillDate: dayjs(),
        colleagueDifficulties: 'no',
      };

  const submitLabel = isEdit ? 'Сохранить' : 'Отправить отчёт';

  const submitButton = (
    <Button type="primary" htmlType="submit" loading={loading} size="large" icon={<SendOutlined />}>
      {submitLabel}
    </Button>
  );

  const submitFab = (
    <div className="form-fab-wrap">
      <Tooltip title={submitLabel} placement="left">
        <Button
          type="primary"
          shape="circle"
          htmlType="submit"
          loading={loading}
          icon={<SendOutlined />}
          className="form-fab"
          aria-label={submitLabel}
        />
      </Tooltip>
    </div>
  );

  return (
    <Form
      form={form}
      layout="vertical"
      className="report-form"
      initialValues={defaultValues}
      onFinish={handleFinish}
      requiredMark={optional ? false : true}
    >
      <FormSection step="1" title="Общая информация">
        <Form.Item
          name="fillDate"
          label={optional ? optionalLabel('Дата заполнения') : 'Дата заполнения'}
          rules={req([{ required: true, message: 'Выберите дату' }])}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD.MM.YYYY"
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
        </Form.Item>
      </FormSection>

      <FormSection
        step="2"
        title="Настроение и самочувствие"
        subtitle="Оцените своё состояние за последние две недели"
      >
        <ScoreSlider
          label="Общее настроение"
          value={moodScore}
          onChange={setMoodScore}
          required={!optional}
        />
        <Form.Item name="moodComment" label={optionalLabel('Причина оценки')}>
          <TextArea rows={2} placeholder="По желанию" />
        </Form.Item>

        <ScoreSlider label="Оценка прошедших двух недель" value={twoWeeksScore} onChange={setTwoWeeksScore} required={!optional} />
        <Form.Item name="twoWeeksComment" label={optionalLabel('Причина оценки')}>
          <TextArea rows={2} placeholder="По желанию" />
        </Form.Item>

        <ScoreSlider label="Уровень нагрузки" value={workloadLevel} onChange={setWorkloadLevel} required={!optional} />
        <ScoreSlider label="Уровень стресса" value={stressLevel} onChange={setStressLevel} required={!optional} />
        <ScoreSlider label="Удовлетворённость своей работой" value={jobSatisfaction} onChange={setJobSatisfaction} required={!optional} />
        <ScoreSlider label="Удовлетворённость работой команды" value={teamSatisfaction} onChange={setTeamSatisfaction} required={!optional} />

        <Form.Item
          name="stateWord"
          label={optional ? optionalLabel('Слово, описывающее состояние') : 'Слово, описывающее состояние'}
          rules={req([{ required: true, message: 'Выберите слово' }])}
        >
          <StateWordPicker />
        </Form.Item>
      </FormSection>

      <FormSection step="3" title="Работа и результаты">
        <MultiSelectField form={form} name="proudResults" label="Результаты, которыми вы гордитесь" options={toSelectOptions(PROUD_RESULTS)} optional={optional} rules={[{ required: true }]} />
        <MultiSelectField form={form} name="wentWell" label="Что получилось особенно хорошо" options={toSelectOptions(WENT_WELL)} otherFieldName="wentWellOther" optional={optional} rules={[{ required: true }]} />
        <MultiSelectField form={form} name="difficulties" label="Причины трудностей в работе" options={toSelectOptions(DIFFICULTIES)} otherFieldName="difficultiesOther" optional={optional} rules={[{ required: true }]} />
      </FormSection>

      <FormSection step="4" title="Взаимодействие и процессы">
        <MultiSelectField form={form} name="helpsEffective" label="Что помогает работать эффективнее" options={toSelectOptions(HELPS_EFFECTIVE)} otherFieldName="helpsEffectiveOther" optional={optional} rules={[{ required: true }]} />
        <MultiSelectField form={form} name="hindersWork" label="Что мешает больше всего" options={toSelectOptions(HINDERS_WORK)} otherFieldName="hindersWorkOther" optional={optional} rules={[{ required: true }]} />
        <Form.Item name="colleagueDifficulties" label={optional ? optionalLabel('Сложности с коллегами') : 'Сложности с коллегами'} rules={req([{ required: true }])}>
          <Select allowClear placeholder="Выберите" options={toSelectOptions(COLLEAGUE_DIFFICULTIES)} />
        </Form.Item>
        {showColleagueIssues && (
          <MultiSelectField form={form} name="colleagueIssues" label="С чем связаны" options={toSelectOptions(COLLEAGUE_ISSUES)} otherFieldName="colleagueIssuesOther" maxCount={0} optional={optional} rules={[{ required: true }]} />
        )}
        <MultiSelectField form={form} name="processesToImprove" label="Какие процессы можно улучшить" options={toSelectOptions(PROCESSES_TO_IMPROVE)} otherFieldName="processesToImproveOther" optional={optional} rules={[{ required: true }]} />
      </FormSection>

      <FormSection step="5" title="Развитие">
        <MultiSelectField form={form} name="recentLearning" label="Чему удалось научиться" options={toSelectOptions(RECENT_LEARNING)} otherFieldName="recentLearningOther" optional={optional} rules={[{ required: true }]} />
        <MultiSelectField form={form} name="developmentSupport" label="Какая поддержка поможет развиваться" options={toSelectOptions(DEVELOPMENT_SUPPORT)} otherFieldName="developmentSupportOther" optional={optional} rules={[{ required: true }]} />
      </FormSection>

      <FormSection step="6" title="Темы для обсуждения">
        <MultiSelectField form={form} name="discussionTopics" label="Вопросы для встречи" options={toSelectOptions(DISCUSSION_TOPICS)} otherFieldName="discussionTopicsOther" maxCount={5} optional={optional} rules={[{ required: true }]} />
        <MultiSelectField form={form} name="feedbackNeeded" label="Темы для обратной связи" options={toSelectOptions(FEEDBACK_NEEDED)} otherFieldName="feedbackNeededOther" maxCount={5} optional={optional} rules={[{ required: true }]} />
        <MultiSelectField form={form} name="managerFeedback" label="Обратная связь для руководителя" options={toSelectOptions(MANAGER_FEEDBACK)} otherFieldName="managerFeedbackOther" maxCount={5} optional={optional} rules={[{ required: true }]} />
      </FormSection>

      <FormSection step="7" title="Ожидание от встречи">
        <MultiSelectField form={form} name="meetingMainResult" label="Главный результат встречи" options={toSelectOptions(MEETING_MAIN_RESULT)} otherFieldName="meetingMainResultOther" optional={optional} rules={[{ required: true }]} />
        <Form.Item name="singleTopicPriority" label={optionalLabel('Если обсудить только одну тему — какую?')}>
          <TextArea rows={2} placeholder="По желанию" />
        </Form.Item>
      </FormSection>

      {variant === 'public' ? (
        submitFab
      ) : (
        <Form.Item style={{ marginTop: 8 }}>
          <Space>{submitButton}</Space>
        </Form.Item>
      )}
    </Form>
  );
};

export default FormBuilder;
